import { API_URL } from '@/core/api/client'

import type { CourseFromAPI } from '../stores/types'

export interface PublicSession {
  id: string
  courseId: string
  courseName: string
  locationName: string
  date: string
  startTime?: string
  endTime?: string
  durationMinutes: number
  instructorName: string
  maxAttendees: number
  spotsAvailable: number
  language?: string
}

export interface CheckoutSessionData {
  courseSlug: string
  sessionId: string
  quantity: number
  email: string
  firstName: string
  lastName: string
  phone?: string
  company?: string
}

export interface CheckoutSessionResponse {
  checkoutUrl: string
  orderId: string
}

/** Fetch all active courses (public, no auth). */
export async function fetchPublicCourses(): Promise<CourseFromAPI[]> {
  const res = await fetch(`${API_URL}/public/courses/`)
  if (!res.ok) {throw new Error('Failed to load courses')}
  const data = await res.json()
  // DRF returns { count, next, previous, results }
  const results = data.results ?? data
  return results.map((c: Record<string, unknown>) => ({
    id: c.id as string,
    name: c.name as string,
    slug: c.slug as string,
    description: (c.description as string) ?? '',
    type: c.type as 'presencial' | 'online',
    durationMinutes: c.duration_minutes as number,
    validYears: c.valid_years as number,
    b2cPrice: c.b2c_price as number | null,
  }))
}

/** Fetch public sessions filtered by course slug and optional date range. */
export async function fetchPublicSessions(
  courseSlug?: string,
  fromDate?: string,
  toDate?: string
): Promise<PublicSession[]> {
  const params = new URLSearchParams()
  if (courseSlug) {params.set('course', courseSlug)}
  if (fromDate) {params.set('from', fromDate)}
  if (toDate) {params.set('to', toDate)}

  const res = await fetch(`${API_URL}/public/sessions/?${params.toString()}`)
  if (!res.ok) {throw new Error('Failed to load sessions')}
  const data = await res.json()
  const results = data.results ?? data
  return results.map((s: Record<string, unknown>) => ({
    id: s.id as string,
    courseId: s.courseId as string,
    courseName: s.course_name as string,
    locationName: s.location_name as string,
    date: s.date as string,
    startTime: s.start_time as string | undefined,
    endTime: s.end_time as string | undefined,
    durationMinutes: s.duration_minutes as number,
    instructorName: s.instructor_name as string,
    maxAttendees: (s.max_attendees as number) ?? 20,
    spotsAvailable: (s.spots_available as number) ?? 20,
    language: s.language as string | undefined,
  }))
}

/** Create a Stripe Checkout Session for B2C booking. */
export async function createCheckoutSession(
  data: CheckoutSessionData
): Promise<CheckoutSessionResponse> {
  const res = await fetch(`${API_URL}/checkout/create_session/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      course_slug: data.courseSlug,
      session_id: data.sessionId,
      quantity: data.quantity,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone ?? '',
      company: data.company ?? '',
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail ?? 'Failed to create checkout session')
  }
  const json = await res.json()
  return {
    checkoutUrl: json.checkout_url,
    orderId: json.order_id,
  }
}

/** Verify a completed Stripe checkout. */
export async function verifyCheckout(sessionId: string): Promise<{
  status: string
  orderId: string
  paymentStatus: string
  customerEmail: string | null
}> {
  const res = await fetch(`${API_URL}/checkout/success/?session_id=${encodeURIComponent(sessionId)}`)
  if (!res.ok) {throw new Error('Failed to verify checkout')}
  const json = await res.json()
  return {
    status: json.status,
    orderId: json.order_id,
    paymentStatus: json.payment_status,
    customerEmail: json.customer_email,
  }
}
